'use client';

import React, { createContext, useContext, useMemo } from 'react';

import { useShoppingCartContext } from '@/components/Providers/ShoppingCartProvider';
import CartListItem from '@/components/V2/Cart/CartListItem';
import { isEnrollmentPackCode } from '@/helpers/utils/enrollmentFlowTracker';
import { CartItem } from '@/types/Cart';

import '@/styles/globals.v2.css';

import DeadState from '../../DeadState';

type CartListChilds = {
  variant: 'default' | 'compact' | 'ezship-compact';
};

const context = createContext<CartListChilds>({ variant: 'default' });
const Provider = context.Provider;

export const useCartListContext = () => useContext(context);

interface CartListProps extends Partial<CartListChilds> {
  cart?: Array<Partial<CartItem>>;
}

const CartList: React.FC<CartListProps> = ({ variant, ...rest }) => {
  let { cart } = useShoppingCartContext();

  if (variant === 'ezship-compact') cart = cart?.filter(cartItem => cartItem.isEZShip);
  const shoppingCart = rest.cart ?? cart;

  const sortedCart = useMemo(() => {
    if (!shoppingCart || shoppingCart.length === 0) return shoppingCart;

    return [...shoppingCart].sort((a, b) => {
      const aCode = String(a.itemCode || a.id);
      const bCode = String(b.itemCode || b.id);
      const aIsEnrollmentPack = isEnrollmentPackCode(aCode);
      const bIsEnrollmentPack = isEnrollmentPackCode(bCode);

      if (aIsEnrollmentPack && !bIsEnrollmentPack) return -1;
      if (!aIsEnrollmentPack && bIsEnrollmentPack) return 1;
      return 0;
    });
  }, [shoppingCart]);

  return (
    <>
      <Provider value={{ variant: variant || 'default' }}>
        <div className="v2-mb-6 v2-hidden xs:v2-hidden sm:v2-flex sm:v2-justify-between v2-gap-3">
          <p className="v2-max-w-full sm:v2-max-w-[400px] md:v2-max-w-[412px] xl:v2-max-w-[471px] lg:v2-max-w-[471px] v2-w-full v2-font-sweet-sans-pro-light v2-font-normal v2-text-base v2-text-[#344054]">
            ITEM
          </p>
          <div className="v2-flex v2-w-[232px] v2-justify-between">
            <p className="v2-font-sweet-sans-pro-light v2-font-normal v2-text-base v2-text-[#344054]">QUANTITY</p>
            <p className="v2-font-sweet-sans-pro-light v2-font-normal v2-text-base v2-text-[#344054]">PRICE</p>
          </div>
        </div>
        <hr />

        {sortedCart && sortedCart.length > 0 ? (
          <ul className="sm:v2-w-full md:v2-w-auto md:v2-flex-grow">
            {sortedCart.map(cartItem => (
              <CartListItem
                key={`${variant}-${cartItem.itemCode || cartItem.id}-${JSON.stringify(cartItem)}`}
                cartItem={cartItem}
              />
            ))}
          </ul>
        ) : (
          <div className="v2-mt-4">
            <DeadState title="No Products in Your Cart" />
          </div>
        )}
      </Provider>
    </>
  );
};

export default CartList;
